import {useEffect} from 'react';

import {useCookies} from '@optimaxdev/utils';
import {useSelector} from 'react-redux';

import {LUXURY_BADGES} from 'constants/badges';

import {IS_LUXURY_EXPERIENCE} from 'constants/success';
import {getOrderLastData} from 'desktop/pages/success/store/selectors/orderLast/orderLast';

import {OrderedProductType} from 'desktop/pages/success/types/types';
import {ApplicationStoreType} from 'mobile/store';

import {OrderDetailsItemsType} from 'types/orderDetailsTypes';

const getLuxuryBadge = (items: Array<OrderedProductType | OrderDetailsItemsType>) => {
    return (
        items?.find(item => LUXURY_BADGES.includes(Number(item?.product?.badge)))?.product?.badge ||
        null
    );
};

const setLuxuryCookie = (value: boolean) => {
    document.cookie = `${IS_LUXURY_EXPERIENCE}=${value ? 1 : ''}`;
};

/**
 * Tracks luxury cookie
 */
export const useLuxuryCookie = ({
    flagState,
    isLuxuryCookieEnabled,
    luxuryBadge,
    status,
}: {
    status: string;
    isLuxuryCookieEnabled: boolean;
    luxuryBadge: Maybe<number>;
    flagState: boolean;
}) => {
    useEffect(() => {
        if (!flagState) return;
        if (status.toLowerCase() === 'fulfilled' && !isLuxuryCookieEnabled && luxuryBadge) {
            setLuxuryCookie(true);
        }
    }, [status, luxuryBadge, isLuxuryCookieEnabled, flagState]);

    useEffect(() => {
        return () => {
            if (isLuxuryCookieEnabled) {
                setLuxuryCookie(false);
            }
        };
    }, [isLuxuryCookieEnabled]);
};

/**
 * Gets data from order last
 */
export const useGetOrderLastData = () => {
    const {items: itemsDesktop, status: statusDesktop} = useSelector(getOrderLastData) || {};
    const itemsMobile = useSelector(
        (store: ApplicationStoreType) => store.successPage?.orderDetails?.items,
    );
    const statusMobile = useSelector(
        (store: ApplicationStoreType) => store.successPage?.orderDetails?.status,
    );

    const items = itemsDesktop || itemsMobile;
    const status = statusDesktop || statusMobile || '';

    return {items, status};
};

/**
 * Tracks the luxury experience on thank you page
 */
export const useCheckLuxuryItem = (flagState: boolean): boolean => {
    const {items, status} = useGetOrderLastData();

    const isLuxuryCookieEnabled = Boolean(useCookies(IS_LUXURY_EXPERIENCE).find(e => e));

    const luxuryBadge = getLuxuryBadge(items);

    useLuxuryCookie({flagState, isLuxuryCookieEnabled, luxuryBadge, status});

    if (isLuxuryCookieEnabled || status.toLowerCase() !== 'fulfilled') {
        return true;
    }

    return Boolean(luxuryBadge && flagState && status.toLowerCase() === 'fulfilled');
};
